import { translate } from '../../i18n/index.js';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AvailableSlot,
  ChangeEffectiveMode,
  CreatePricingRuleInput,
  Gym,
  PricingTier,
  RecurringSlotBlock,
} from '@atomfit/shared';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import {
  CustomSelect,
  Spinner,
  Tag,
  TierPill,
  formatCurrency,
  formatTime,
} from '../../components/design/AtomFitUI.js';
import { gymsApi } from '../../api/gyms.api.js';
import {
  pricingApi,
  type PricingAuditLog,
  type PricingRuleWithGym,
} from '../../api/pricing.api.js';
import { slotsApi } from '../../api/slots.api.js';
import { useAuth } from '../../hooks/useAuth.js';
import { formatLocalDate } from '../../utils/date.js';

const DAYS = [
  translate('pages.admin.admin_slots_page.module.days'),
  translate('pages.admin.admin_slots_page.module.days_2'),
  translate('pages.admin.admin_slots_page.module.days_3'),
  translate('pages.admin.admin_slots_page.module.days_4'),
  translate('pages.admin.admin_slots_page.module.days_5'),
  translate('pages.admin.admin_slots_page.module.days_6'),
  translate('pages.admin.admin_slots_page.module.days_7'),
];

const TIERS: PricingTier[] = ['STANDARD', 'OFF_PEAK', 'PEAK', 'LATE_NIGHT'];
const TIER_LABEL: Record<PricingTier, string> = {
  STANDARD: translate('pages.admin.admin_slots_page.tier_label.standard'),
  OFF_PEAK: translate('pages.admin.admin_slots_page.tier_label.off_peak'),
  PEAK: translate('pages.admin.admin_slots_page.tier_label.peak'),
  LATE_NIGHT: translate('pages.admin.admin_slots_page.tier_label.late_night'),
};

interface RuleForm {
  gymId: string;
  name: string;
  tier: PricingTier;
  price: string;
  currency: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  priority: string;
  effectiveMode: ChangeEffectiveMode;
}

const emptyRule: RuleForm = {
  gymId: '',
  name: '',
  tier: 'OFF_PEAK',
  price: '199',
  currency: 'CZK',
  dayOfWeek: '',
  startTime: '',
  endTime: '',
  priority: '0',
  effectiveMode: 'IMMEDIATE',
};

function todayIso() {
  return formatLocalDate(new Date());
}

function addDays(date: string, amount: number) {
  const next = new Date(`${date}T12:00:00`);
  next.setDate(next.getDate() + amount);
  return formatLocalDate(next);
}

function addMonths(date: string, amount: number) {
  const next = new Date(`${date}T12:00:00`);
  next.setMonth(next.getMonth() + amount);
  return formatLocalDate(next);
}

function weekStart(date: string) {
  const d = new Date(`${date}T12:00:00`);
  const day = d.getDay();
  d.setDate(d.getDate() - ((day + 6) % 7));
  return formatLocalDate(d);
}

function toRuleInput(form: RuleForm): CreatePricingRuleInput {
  return {
    gymId: form.gymId || undefined,
    name: form.name.trim(),
    tier: form.tier,
    priceInSmallestUnit: Math.round(Number(form.price || 0) * 100),
    currency: form.currency.toUpperCase(),
    dayOfWeek: form.dayOfWeek === '' ? undefined : Number(form.dayOfWeek),
    startTime: form.startTime || undefined,
    endTime: form.endTime || undefined,
    priority: Number(form.priority || 0),
    effectiveMode: form.effectiveMode,
  };
}

function slotStatus(slot: AvailableSlot) {
  if (slot.status === 'BLOCKED')
    return {
      label: translate('pages.admin.admin_slots_page.slot_status.blocked'),
      color: 'var(--af-danger)',
      bg: 'rgba(232,66,26,0.1)',
    };
  if (slot.status === 'FULL')
    return {
      label: translate('pages.admin.admin_slots_page.slot_status.label'),
      color: 'var(--af-danger)',
      bg: 'rgba(232,66,26,0.1)',
    };
  if (slot.status === 'LIMITED')
    return {
      label: translate('pages.admin.admin_slots_page.slot_status.label_2'),
      color: 'var(--af-peak)',
      bg: 'var(--af-peak-soft)',
    };
  return {
    label: translate('pages.admin.admin_slots_page.slot_status.label_3'),
    color: 'var(--af-success)',
    bg: 'rgba(74,222,128,0.1)',
  };
}

export function AdminSlotsPage() {
  const { role } = useAuth();
  const canEditSlotsAndPricing = role === 'PLATFORM_ADMIN';
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [gymId, setGymId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [weekSlots, setWeekSlots] = useState<Record<string, AvailableSlot[]>>({});
  const [rules, setRules] = useState<PricingRuleWithGym[]>([]);
  const [audit, setAudit] = useState<PricingAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [form, setForm] = useState<RuleForm>(emptyRule);
  const [selectingSlots, setSelectingSlots] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
  const [blockResult, setBlockResult] = useState<string | null>(null);
  const [recurringBlocks, setRecurringBlocks] = useState<RecurringSlotBlock[]>([]);
  const [recurringDays, setRecurringDays] = useState<number[]>([]);
  const [recurringStartTime, setRecurringStartTime] = useState('');
  const [recurringEndTime, setRecurringEndTime] = useState('');
  const [recurringFrom, setRecurringFrom] = useState(todayIso());
  const [recurringUntil, setRecurringUntil] = useState('');
  const [recurringReason, setRecurringReason] = useState('');
  const [blockReason, setBlockReason] = useState('Maintenance');
  const [blockMode, setBlockMode] = useState<ChangeEffectiveMode>('IMMEDIATE');
  const [message, setMessage] = useState<string | null>(null);

  const start = useMemo(() => weekStart(date), [date]);
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(start, index)),
    [start],
  );
  const selectedGym = useMemo(() => gyms.find((gym) => gym.id === gymId), [gyms, gymId]);

  useEffect(() => {
    setSelectedSlots(new Set());
    setBlockResult(null);
  }, [gymId, start]);

  const selectableSlots = Object.values(weekSlots)
    .flat()
    .filter((slot) => slot.capacityBooked === 0 && slot.status !== 'BLOCKED');

  const loadGyms = useCallback(async () => {
    const res = await gymsApi.adminList(role ?? '', 1);
    setGyms(res.data.data);
    setGymId((current) => current || res.data.data[0]?.id || '');
  }, [role]);

  useEffect(() => {
    void loadGyms();
  }, [loadGyms]);

  const load = useCallback(async () => {
    if (!gymId) return;
    setLoading(true);
    try {
      const [ruleRes, recurringRes, slotResponses] = await Promise.all([
        pricingApi.list(gymId, 1),
        canEditSlotsAndPricing
          ? slotsApi.recurringBlocks(gymId)
          : Promise.resolve({ data: { data: [] as RecurringSlotBlock[] } }),
        Promise.all(weekDates.map((day) => slotsApi.list(gymId, day))),
      ]);
      const auditRes = await pricingApi.audit(1, gymId);
      setRules(ruleRes.data.data);
      setRecurringBlocks(recurringRes.data.data);
      setAudit(auditRes.data.data);
      setWeekSlots(
        Object.fromEntries(
          weekDates.map((day, index) => [day, slotResponses[index]?.data.data ?? []]),
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [canEditSlotsAndPricing, gymId, weekDates]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveRule(event: React.FormEvent) {
    event.preventDefault();
    if (!canEditSlotsAndPricing) return;
    setSaving(true);
    setMessage(null);
    try {
      if (editingRuleId) {
        await pricingApi.update(editingRuleId, toRuleInput(form));
      } else {
        await pricingApi.create(toRuleInput(form));
      }
      setForm({ ...emptyRule, gymId: form.gymId });
      setEditingRuleId(null);
      setMessage(translate('pages.admin.admin_slots_page.save_rule.set_message.save'));
      await load();
    } catch (err: unknown) {
      setMessage(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          translate('pages.admin.admin_slots_page.save_rule.set_message.error_save'),
      );
    } finally {
      setSaving(false);
    }
  }

  function editRule(rule: PricingRuleWithGym) {
    if (!canEditSlotsAndPricing) return;
    setEditingRuleId(rule.id);
    setForm({
      gymId: rule.gymId ?? '',
      name: rule.name,
      tier: rule.tier,
      price: String(rule.priceInSmallestUnit / 100),
      currency: rule.currency,
      dayOfWeek: rule.dayOfWeek === null ? '' : String(rule.dayOfWeek),
      startTime: rule.startTime ?? '',
      endTime: rule.endTime ?? '',
      priority: String(rule.priority),
      effectiveMode: 'IMMEDIATE',
    });
  }

  async function toggleBlock(slot: AvailableSlot) {
    if (!canEditSlotsAndPricing || !gymId || slot.capacityBooked > 0) return;
    setSaving(true);
    setMessage(null);
    try {
      if (slot.status === 'BLOCKED') {
        await slotsApi.unblock({ gymId, startTime: new Date(slot.startTime).toISOString() });
      } else {
        await slotsApi.block({
          gymId,
          startTime: new Date(slot.startTime).toISOString(),
          reason: blockReason,
          effectiveMode: blockMode,
        });
      }
      await load();
    } catch (err: unknown) {
      setMessage(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          translate('pages.admin.admin_slots_page.toggle_block.set_message.error'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function blockSelectedSlots() {
    if (!canEditSlotsAndPricing || !gymId || saving || loading || !blockReason.trim()) return;
    const targets = selectableSlots.filter((slot) => selectedSlots.has(String(slot.startTime)));
    if (!targets.length) return;
    setSaving(true);
    setMessage(null);
    setBlockResult(null);
    const failed = new Set<string>();
    let firstError: string | undefined;
    try {
      for (const slot of targets) {
        try {
          await slotsApi.block({
            gymId,
            startTime: new Date(slot.startTime).toISOString(),
            reason: blockReason.trim(),
            effectiveMode: blockMode,
          });
        } catch (err: unknown) {
          failed.add(String(slot.startTime));
          firstError ??= (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message;
        }
      }
      setSelectedSlots(failed);
      setBlockResult(
        translate('pages.admin.admin_slots_page.bulk.result', {
          blocked: targets.length - failed.size,
          failed: failed.size,
        }),
      );
      if (failed.size) {
        setMessage(
          firstError ?? translate('pages.admin.admin_slots_page.toggle_block.set_message.error'),
        );
      }
      await load();
    } catch {
      setMessage(translate('pages.admin.admin_slots_page.bulk.refresh_error'));
    } finally {
      setSaving(false);
    }
  }

  async function saveRecurringBlock(event: React.FormEvent) {
    event.preventDefault();
    if (!canEditSlotsAndPricing || !gymId || !recurringDays.length) return;
    setSaving(true);
    setMessage(null);
    try {
      await slotsApi.createRecurringBlock({
        gymId,
        daysOfWeek: recurringDays,
        startTime: recurringStartTime,
        endTime: recurringEndTime,
        reason: recurringReason.trim(),
        effectiveFrom: recurringFrom,
        ...(recurringUntil ? { effectiveUntil: recurringUntil } : {}),
      });
      setMessage(translate('pages.admin.admin_slots_page.recurring.saved'));
      await load();
    } catch (err: unknown) {
      setMessage(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          translate('pages.admin.admin_slots_page.recurring.error'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function removeRecurringBlock(ruleId: string) {
    if (!canEditSlotsAndPricing) return;
    setSaving(true);
    setMessage(null);
    try {
      await slotsApi.deleteRecurringBlock(ruleId);
      await load();
    } catch (err: unknown) {
      setMessage(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          translate('pages.admin.admin_slots_page.recurring.error'),
      );
    } finally {
      setSaving(false);
    }
  }

  const eyebrow =
    role === 'PLATFORM_ADMIN'
      ? translate('pages.admin.admin_slots_page.admin_slots_page.eyebrow')
      : translate('pages.admin.admin_slots_page.admin_slots_page.eyebrow_2');

  return (
    <AdminLayout
      title={translate('pages.admin.admin_slots_page.admin_slots_page.admin_layout.slot_pricing')}
      eyebrow={eyebrow}
    >
      <div className="af-card" style={{ padding: 16, marginBottom: 16 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(220px, 1fr) 180px repeat(4, auto)',
            gap: 12,
            alignItems: 'end',
          }}
        >
          <label className="af-label">
            {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.gym')}
            <CustomSelect
              value={gymId}
              disabled={saving}
              onChange={(value) => {
                if (!saving) setGymId(value);
              }}
              options={gyms.map((gym) => ({ value: gym.id, label: gym.name }))}
            />
          </label>
          <label className="af-label">
            {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.time')}
            <input
              className="af-input"
              type="date"
              disabled={saving}
              value={start}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <button
            className="af-outline-btn"
            style={{ padding: '12px 16px' }}
            disabled={saving}
            onClick={() => setDate(addDays(start, -7))}
          >
            {translate('pages.admin.admin_slots_page.admin_slots_page.button_outline_btn.previous')}
          </button>
          <button
            className="af-outline-btn"
            style={{ padding: '12px 16px' }}
            disabled={saving}
            onClick={() => setDate(addDays(start, 7))}
          >
            {translate('pages.admin.admin_slots_page.admin_slots_page.button_outline_btn.next')}
          </button>
          <button
            className="af-outline-btn"
            style={{ padding: '12px 16px' }}
            disabled={saving}
            onClick={() => setDate(addMonths(start, -1))}
          >
            {translate('pages.admin.admin_slots_page.horizon.previous_month')}
          </button>
          <button
            className="af-outline-btn"
            style={{ padding: '12px 16px' }}
            disabled={saving}
            onClick={() => setDate(addMonths(start, 1))}
          >
            {translate('pages.admin.admin_slots_page.horizon.next_month')}
          </button>
        </div>
      </div>

      {message && (
        <div
          className={message.includes('ulož') ? '' : 'af-error'}
          style={
            message.includes('ulož')
              ? {
                  marginBottom: 16,
                  padding: '10px 14px',
                  borderRadius: 8,
                  background: 'rgba(74,222,128,0.1)',
                  color: 'var(--af-success)',
                  fontSize: 13,
                }
              : { marginBottom: 16 }
          }
        >
          {message}
        </div>
      )}

      <div className="af-admin-main-grid">
        <section
          className="af-card"
          style={{ gridColumn: '1 / -1', padding: 20, overflow: 'auto' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 800 }}>
                {translate('pages.admin.admin_slots_page.admin_slots_page.div.calendar')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--af-dim)', marginTop: 3 }}>
                {selectedGym?.name ??
                  translate('pages.admin.admin_slots_page.admin_slots_page.div.gym_select')}
              </div>
            </div>
            <Tag>
              {Object.values(weekSlots).flat().length}{' '}
              {translate('pages.admin.admin_slots_page.admin_slots_page.tag.slot')}
            </Tag>
          </div>
          {canEditSlotsAndPricing && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 150px 170px',
                gap: 10,
                marginBottom: 14,
              }}
            >
              <label className="af-label">
                {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.reason')}
                <input
                  className="af-input"
                  maxLength={240}
                  disabled={saving}
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.copy')}
                <CustomSelect
                  value={blockMode}
                  disabled={saving}
                  onChange={(value) => setBlockMode(value as ChangeEffectiveMode)}
                  options={[
                    {
                      value: 'IMMEDIATE',
                      label: translate(
                        'pages.admin.admin_slots_page.admin_slots_page.custom_select.options',
                      ),
                    },
                    {
                      value: 'NEXT_DAY',
                      label: translate(
                        'pages.admin.admin_slots_page.admin_slots_page.custom_select.options_2',
                      ),
                    },
                  ]}
                />
              </label>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--af-muted)',
                  alignSelf: 'end',
                  paddingBottom: 10,
                }}
              >
                {translate('pages.admin.admin_slots_page.admin_slots_page.div.booking_slot')}
              </div>
            </div>
          )}
          {canEditSlotsAndPricing && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 10,
                marginBottom: 14,
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={selectingSlots}
                  disabled={saving || loading}
                  onChange={(event) => {
                    setSelectingSlots(event.target.checked);
                    setSelectedSlots(new Set());
                    setBlockResult(null);
                  }}
                />

                {translate('pages.admin.admin_slots_page.bulk.select_mode')}
              </label>
              {selectingSlots && (
                <>
                  <button
                    className="af-outline-btn"
                    style={{ padding: '8px 12px' }}
                    disabled={saving || loading || !selectableSlots.length}
                    onClick={() =>
                      setSelectedSlots(
                        new Set(selectableSlots.map((slot) => String(slot.startTime))),
                      )
                    }
                  >
                    {translate('pages.admin.admin_slots_page.bulk.select_all')}
                  </button>
                  <button
                    className="af-outline-btn"
                    style={{ padding: '8px 12px' }}
                    disabled={saving || loading || !selectedSlots.size}
                    onClick={() => setSelectedSlots(new Set())}
                  >
                    {translate('pages.admin.admin_slots_page.bulk.clear')}
                  </button>
                  <span aria-live="polite">
                    {translate('pages.admin.admin_slots_page.bulk.selected', {
                      count: selectedSlots.size,
                    })}
                  </span>
                  <button
                    className="af-primary-btn"
                    style={{ padding: '8px 12px' }}
                    disabled={saving || loading || !selectedSlots.size || !blockReason.trim()}
                    onClick={() => void blockSelectedSlots()}
                  >
                    {saving ? <Spinner /> : translate('pages.admin.admin_slots_page.bulk.block')}
                  </button>
                </>
              )}
              {blockResult && <span role="status">{blockResult}</span>}
            </div>
          )}
          {loading ? (
            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                padding: 24,
                color: 'var(--af-muted)',
              }}
            >
              <Spinner />{' '}
              {translate('pages.admin.admin_slots_page.admin_slots_page.div.loading_slot')}
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(132px, 1fr))',
                gap: 8,
              }}
            >
              {weekDates.map((day) => (
                <div key={day} style={{ minWidth: 132 }}>
                  <div style={{ padding: '8px 4px', fontSize: 12, fontWeight: 800 }}>
                    {DAYS[new Date(`${day}T12:00:00`).getDay()]}{' '}
                    <span style={{ color: 'var(--af-muted)' }}>{day.slice(5)}</span>
                  </div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {(weekSlots[day] ?? []).map((slot) => {
                      const status = slotStatus(slot);
                      const cannotBlock =
                        !canEditSlotsAndPricing ||
                        slot.capacityBooked > 0 ||
                        Boolean(slot.recurringBlockId) ||
                        saving ||
                        (selectingSlots && slot.status === 'BLOCKED');
                      const selected = selectingSlots && selectedSlots.has(String(slot.startTime));
                      return (
                        <button
                          key={String(slot.startTime)}
                          className="af-outline-btn"
                          style={{
                            padding: 8,
                            borderColor: selected ? 'var(--af-accent)' : undefined,
                            background: selected ? 'var(--af-accent-soft)' : undefined,
                            textAlign: 'left',
                            opacity: cannotBlock && slot.status !== 'BLOCKED' ? 0.55 : 1,
                          }}
                          disabled={cannotBlock}
                          aria-pressed={selectingSlots ? selected : undefined}
                          onClick={() => {
                            if (!selectingSlots) {
                              void toggleBlock(slot);
                              return;
                            }
                            setSelectedSlots((current) => {
                              const next = new Set(current);
                              const key = String(slot.startTime);
                              if (next.has(key)) next.delete(key);
                              else next.add(key);
                              return next;
                            });
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                            <strong>{formatTime(slot.startTime)}</strong>
                            <Tag color={status.color} bg={status.bg}>
                              {status.label}
                            </Tag>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 6,
                              marginTop: 7,
                              fontSize: 11,
                              color: 'var(--af-muted)',
                            }}
                          >
                            <span>
                              {slot.capacityBooked}
                              {'/'}
                              {slot.capacityTotal}
                            </span>
                            <span>{formatCurrency(slot.priceInSmallestUnit, slot.currency)}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {canEditSlotsAndPricing && (
        <section className="af-card" style={{ marginTop: 16, padding: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>
            {translate('pages.admin.admin_slots_page.recurring.title')}
          </div>
          <div style={{ color: 'var(--af-muted)', fontSize: 12, marginTop: 4, marginBottom: 16 }}>
            {translate('pages.admin.admin_slots_page.recurring.gym_slot')}
          </div>
          <form
            onSubmit={(event) => void saveRecurringBlock(event)}
            style={{ display: 'grid', gap: 14 }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {DAYS.map((day, dayOfWeek) => (
                <label key={dayOfWeek} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={recurringDays.includes(dayOfWeek)}
                    disabled={saving}
                    onChange={(event) =>
                      setRecurringDays((current) =>
                        event.target.checked
                          ? [...current, dayOfWeek].sort()
                          : current.filter((value) => value !== dayOfWeek),
                      )
                    }
                  />

                  {day}
                </label>
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(2, minmax(110px, 140px)) repeat(2, minmax(150px, 180px)) minmax(180px, 1fr) auto',
                gap: 12,
                alignItems: 'end',
              }}
            >
              <label className="af-label">
                {translate('pages.admin.admin_slots_page.recurring.from_time')}
                <input
                  className="af-input"
                  type="time"
                  value={recurringStartTime}
                  disabled={saving}
                  onChange={(event) => setRecurringStartTime(event.target.value)}
                  required
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_slots_page.recurring.to_time')}
                <input
                  className="af-input"
                  type="time"
                  value={recurringEndTime}
                  disabled={saving}
                  onChange={(event) => setRecurringEndTime(event.target.value)}
                  required
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_slots_page.recurring.from_date')}
                <input
                  className="af-input"
                  type="date"
                  value={recurringFrom}
                  disabled={saving}
                  onChange={(event) => setRecurringFrom(event.target.value)}
                  required
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_slots_page.recurring.until_date')}
                <input
                  className="af-input"
                  type="date"
                  min={recurringFrom}
                  value={recurringUntil}
                  disabled={saving}
                  onChange={(event) => setRecurringUntil(event.target.value)}
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.reason')}
                <input
                  className="af-input"
                  maxLength={240}
                  value={recurringReason}
                  disabled={saving}
                  onChange={(event) => setRecurringReason(event.target.value)}
                  required
                />
              </label>
              <button
                className="af-primary-btn"
                style={{ padding: '11px 18px' }}
                disabled={
                  saving ||
                  !recurringDays.length ||
                  recurringEndTime <= recurringStartTime ||
                  !recurringReason.trim()
                }
              >
                {saving ? <Spinner /> : translate('pages.admin.admin_slots_page.recurring.add')}
              </button>
            </div>
          </form>
          <div style={{ display: 'grid', gap: 8, marginTop: 18 }}>
            {recurringBlocks.length === 0 ? (
              <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>
                {translate('pages.admin.admin_slots_page.recurring.empty')}
              </div>
            ) : (
              recurringBlocks.map((rule) => (
                <div
                  key={rule.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: 12,
                    border: '1px solid var(--af-border)',
                    borderRadius: 8,
                  }}
                >
                  <div>
                    <strong>{rule.daysOfWeek.map((day) => DAYS[day]).join(', ')}</strong>
                    <span style={{ marginLeft: 10 }}>
                      {rule.startTime}
                      {'–'}
                      {rule.endTime}
                    </span>
                    <div style={{ color: 'var(--af-muted)', fontSize: 12, marginTop: 3 }}>
                      {rule.reason} {'·'} {new Date(rule.effectiveFrom).toLocaleDateString()} {'–'}{' '}
                      {rule.effectiveUntil
                        ? new Date(rule.effectiveUntil).toLocaleDateString()
                        : translate('pages.admin.admin_slots_page.recurring.forever')}
                    </div>
                  </div>
                  <button
                    className="af-outline-btn"
                    style={{ padding: '7px 12px' }}
                    disabled={saving}
                    onClick={() => void removeRecurringBlock(rule.id)}
                  >
                    {translate('pages.admin.admin_slots_page.recurring.deactivate')}
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      <div className="af-admin-main-grid" style={{ marginTop: 16 }}>
        <section
          className="af-card"
          style={{ padding: 20, gridColumn: canEditSlotsAndPricing ? undefined : '1 / -1' }}
        >
          <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 14 }}>
            {translate('pages.admin.admin_slots_page.admin_slots_page.div.create')}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {[
                  translate('pages.admin.admin_slots_page.admin_slots_page.tr.slot'),
                  ...TIERS.map((tier) => TIER_LABEL[tier]),
                ].map((heading) => (
                  <th
                    key={heading}
                    style={{
                      padding: 10,
                      textAlign: 'left',
                      color: 'var(--af-muted)',
                      borderBottom: '1px solid var(--af-border)',
                    }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: 10, fontWeight: 800 }}>
                  {selectedGym?.slotDurationMinutes ?? 60}{' '}
                  {translate('pages.admin.admin_slots_page.admin_slots_page.td.copy')}
                </td>
                {TIERS.map((tier) => (
                  <td key={tier} style={{ padding: 10 }}>
                    {tierPrice(rules, tier)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </section>

        {canEditSlotsAndPricing && (
          <section className="af-card" style={{ padding: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 14 }}>
              {editingRuleId
                ? translate('pages.admin.admin_slots_page.admin_slots_page.div.edit')
                : translate('pages.admin.admin_slots_page.admin_slots_page.div.copy')}
            </div>
            <form onSubmit={(event) => void saveRule(event)} style={{ display: 'grid', gap: 12 }}>
              <label className="af-label">
                {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.copy_2')}
                <input
                  className="af-input"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder={translate(
                    'pages.admin.admin_slots_page.admin_slots_page.input_input.placeholder_content',
                  )}
                  required
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label className="af-label">
                  {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.gym_2')}
                  <CustomSelect
                    value={form.gymId}
                    onChange={(gymId) => setForm((p) => ({ ...p, gymId }))}
                    options={[
                      {
                        value: '',
                        label: translate(
                          'pages.admin.admin_slots_page.admin_slots_page.custom_select.options_3',
                        ),
                      },
                      ...gyms.map((gym) => ({ value: gym.id, label: gym.name })),
                    ]}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.copy_3')}
                  <CustomSelect
                    value={form.tier}
                    onChange={(tier) => setForm((p) => ({ ...p, tier: tier as PricingTier }))}
                    options={TIERS.map((tier) => ({ value: tier, label: TIER_LABEL[tier] }))}
                  />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 110px', gap: 12 }}>
                <label className="af-label">
                  {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.price')}
                  <input
                    className="af-input"
                    type="number"
                    min={0}
                    value={form.price}
                    onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.currency')}
                  <input
                    className="af-input"
                    value={form.currency}
                    maxLength={3}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))
                    }
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.copy_4')}
                  <CustomSelect
                    value={form.effectiveMode}
                    onChange={(effectiveMode) =>
                      setForm((p) => ({
                        ...p,
                        effectiveMode: effectiveMode as ChangeEffectiveMode,
                      }))
                    }
                    options={[
                      {
                        value: 'IMMEDIATE',
                        label: translate(
                          'pages.admin.admin_slots_page.admin_slots_page.custom_select.options_4',
                        ),
                      },
                      {
                        value: 'NEXT_DAY',
                        label: translate(
                          'pages.admin.admin_slots_page.admin_slots_page.custom_select.options_5',
                        ),
                      },
                    ]}
                  />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px', gap: 12 }}>
                <label className="af-label">
                  {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.copy_5')}
                  <CustomSelect
                    value={form.dayOfWeek}
                    onChange={(dayOfWeek) => setForm((p) => ({ ...p, dayOfWeek }))}
                    options={[
                      {
                        value: '',
                        label: translate(
                          'pages.admin.admin_slots_page.admin_slots_page.custom_select.options_6',
                        ),
                      },
                      ...DAYS.map((day, index) => ({ value: String(index), label: day })),
                    ]}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.copy_6')}
                  <input
                    className="af-input"
                    type="time"
                    value={form.startTime}
                    onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.copy_7')}
                  <input
                    className="af-input"
                    type="time"
                    value={form.endTime}
                    onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
                  />
                </label>
                <label className="af-label">
                  {translate('pages.admin.admin_slots_page.admin_slots_page.label_label.priority')}
                  <input
                    className="af-input"
                    type="number"
                    min={0}
                    value={form.priority}
                    onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
                  />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  className="af-primary-btn"
                  style={{ padding: '11px 18px', fontSize: 13 }}
                  disabled={saving}
                >
                  {saving ? (
                    <Spinner />
                  ) : editingRuleId ? (
                    translate(
                      'pages.admin.admin_slots_page.admin_slots_page.button_primary_btn.save',
                    )
                  ) : (
                    translate(
                      'pages.admin.admin_slots_page.admin_slots_page.button_primary_btn.add',
                    )
                  )}
                </button>
                {editingRuleId && (
                  <button
                    className="af-outline-btn"
                    type="button"
                    style={{ padding: '11px 18px' }}
                    onClick={() => {
                      setEditingRuleId(null);
                      setForm(emptyRule);
                    }}
                  >
                    {translate(
                      'pages.admin.admin_slots_page.admin_slots_page.button_outline_btn.cancel',
                    )}
                  </button>
                )}
              </div>
            </form>
          </section>
        )}
      </div>

      <section className="af-card" style={{ marginTop: 16, padding: 20, overflow: 'auto' }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 14 }}>
          {translate('pages.admin.admin_slots_page.admin_slots_page.div.copy_2')}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {rules.map((rule) => (
              <tr
                key={rule.id}
                style={{
                  borderTop: '1px solid var(--af-border)',
                  opacity: rule.isActive ? 1 : 0.5,
                }}
              >
                <td style={{ padding: '13px 10px', fontWeight: 800 }}>
                  {rule.name}
                  <div style={{ color: 'var(--af-dim)', fontSize: 11 }}>
                    {rule.gym?.name ??
                      translate('pages.admin.admin_slots_page.admin_slots_page.div.gym')}
                  </div>
                </td>
                <td style={{ padding: '13px 10px' }}>
                  <TierPill tier={rule.tier} />
                </td>
                <td style={{ padding: '13px 10px', color: 'var(--af-muted)' }}>
                  {formatCurrency(rule.priceInSmallestUnit, rule.currency)}
                </td>
                <td style={{ padding: '13px 10px', color: 'var(--af-muted)' }}>
                  {rule.dayOfWeek === null
                    ? translate('pages.admin.admin_slots_page.admin_slots_page.td.copy_2')
                    : DAYS[rule.dayOfWeek]}{' '}
                  {rule.startTime ?? '--:--'}
                  {'-'}
                  {rule.endTime ?? '--:--'}
                </td>
                <td style={{ padding: '13px 10px', color: 'var(--af-muted)' }}>
                  {translate('pages.admin.admin_slots_page.admin_slots_page.td.copy_3')}{' '}
                  {new Date(rule.effectiveFrom).toLocaleString('cs-CZ')}
                </td>
                {canEditSlotsAndPricing && (
                  <td style={{ padding: '13px 10px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <button
                        className="af-outline-btn"
                        style={{ padding: '6px 12px' }}
                        disabled={!rule.isActive}
                        onClick={() => editRule(rule)}
                      >
                        {translate(
                          'pages.admin.admin_slots_page.admin_slots_page.button_outline_btn.edit',
                        )}
                      </button>
                      <button
                        className="af-outline-btn"
                        style={{ padding: '6px 12px' }}
                        disabled={!rule.isActive}
                        onClick={() => void pricingApi.delete(rule.id).then(load)}
                      >
                        {translate(
                          'pages.admin.admin_slots_page.admin_slots_page.button_outline_btn.inactive',
                        )}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="af-card" style={{ marginTop: 16, padding: 20, overflow: 'auto' }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 14 }}>
          {translate('pages.admin.admin_slots_page.admin_slots_page.div.history')}
        </div>
        {audit.length === 0 ? (
          <div style={{ padding: '18px 8px', color: 'var(--af-muted)', fontSize: 13 }}>
            {translate('pages.admin.admin_slots_page.admin_slots_page.div.copy_3')}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {audit.map((item) => (
                <tr key={item.id} style={{ borderTop: '1px solid var(--af-border)' }}>
                  <td style={{ padding: '12px 10px', fontWeight: 800 }}>{item.action}</td>
                  <td style={{ padding: '12px 10px', color: 'var(--af-muted)' }}>
                    {item.entityType}
                  </td>
                  <td style={{ padding: '12px 10px', color: 'var(--af-muted)' }}>
                    {item.actor?.email ??
                      translate('pages.admin.admin_slots_page.admin_slots_page.td.copy_4')}
                  </td>
                  <td
                    style={{ padding: '12px 10px', color: 'var(--af-muted)', textAlign: 'right' }}
                  >
                    {new Date(item.createdAt).toLocaleString('cs-CZ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </AdminLayout>
  );
}

function tierPrice(rules: PricingRuleWithGym[], tier: PricingTier) {
  const rule = rules.find((item) => item.tier === tier && item.isActive);
  return rule ? formatCurrency(rule.priceInSmallestUnit, rule.currency) : '-';
}
